import type { ReactNode } from "react";
import { useKeycloakContext } from "@/auth/KeycloakProvider";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

// Set at build time by Vite (.env.test, CI, or -D flag) to short-circuit
// the Keycloak dance during e2e runs. Production builds never pick this
// up because it is off by default and only ever read here.
const E2E_BYPASS_AUTH = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: string[];
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { keycloak, initialized } = useKeycloakContext();

  // E2E short-circuit: skip Keycloak entirely. Role checks are bypassed
  // too — the flag is expected to be set alongside a mock API layer in
  // the test environment so specs can drive every page directly.
  if (E2E_BYPASS_AUTH) {
    return <>{children}</>;
  }

  if (!initialized) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-950">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!keycloak.authenticated) {
    keycloak.login();
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-950">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-400">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Check for required roles if specified
  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.some((role) => keycloak.hasRealmRole(role));
    if (!hasRequiredRole) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-gray-950">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-400">Access Denied</h1>
            <p className="mt-2 text-gray-400">
              You do not have the required permissions to access this page.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
